/**
 * Ticket LM1: Landingpage eines Freebies.
 *
 * Eine Route für alle Freebies. Nur veröffentlichte Angebote sind erreichbar,
 * Entwürfe liefern die 404-Seite — so kann nichts versehentlich live gehen.
 *
 * Ticket LM8: Über dem Formular steht dasselbe Vorschaubild wie in der
 * Übersicht (fehlt es, erscheint hier nichts – kein Platzhalter). Unter dem
 * Formularbereich trägt ein ausformulierter Fließtext die Seite für
 * Suchmaschinen. Titel und Beschreibung für Suchmaschinen sind pflegbar und
 * fallen ohne Eintrag auf die bisherige Ableitung zurück.
 */

import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Layout } from "@/components/Layout";
import { LeadMagnetForm } from "@/components/lead-magnets/LeadMagnetForm";
import { OfferPeople } from "@/components/OfferPeople";
import { getLeadMagnetPage } from "@/lib/lead-magnets.functions";
import { leadMagnetHeadMeta } from "@/lib/lead-magnets-meta";
import { normalizeMarkdown } from "@/lib/markdown-normalize";



export const Route = createFileRoute("/kostenlos/$slug/")({
  // Ticket W14: Herkunftskennzeichnung der verweisenden Produktseite (?von=...).
  validateSearch: (search: Record<string, unknown>): { von?: string } => ({
    von: typeof search.von === "string" ? search.von : undefined,
  }),
  loader: async ({ params }) => {
    const magnet = await getLeadMagnetPage({ data: { slug: params.slug } });
    if (!magnet) throw notFound();
    return { magnet };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Nicht verfügbar | creategy" }, { name: "robots", content: "noindex" }],
      };
    }
    // Ticket LM11: Titel, Beschreibung und Vorschaubild kommen aus einer
    // gemeinsamen Stelle – dieselbe Bauweise wie bei den Veranstaltungen.
    return { meta: leadMagnetHeadMeta(loaderData.magnet) };
  },
  component: LeadMagnetLandingPage,
});

function LeadMagnetLandingPage() {
  const { magnet } = Route.useLoaderData();

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Link
            to="/wissen/downloads"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Zurück zur Übersicht
          </Link>

          <h1 className="heading-h1 text-primary">{magnet.title}</h1>


          <div className="mt-10 grid gap-10 lg:mt-14 lg:grid-cols-[1fr_2fr] lg:gap-16">
            <div className="lg:sticky lg:top-24 lg:self-start">
              {magnet.previewImageUrl && (
                <img
                  src={magnet.previewImageUrl}
                  alt={magnet.title}
                  width={900}
                  height={563}
                  loading="eager"
                  decoding="async"
                  className="mx-auto w-[70%] rounded-sm object-contain lg:mx-0 lg:w-full lg:max-h-[calc(100vh-12rem)]"
                />
              )}
            </div>

            <div className="space-y-10 lg:space-y-16">
              {magnet.bodyMd && (
                <div className="markdown-article markdown-article--wide font-body text-foreground">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizeMarkdown(magnet.bodyMd)}</ReactMarkdown>
                </div>
              )}

              <div className="rounded-sm border border-foreground/15 p-6 md:p-8">
              {/* Freigabe 22.09.2026: Bei Werkzeugen ist die Anmeldung ein
                  Konto in der Plattform – die Überschrift sagt das aus. */}
              <h2 className="heading-prose-h3 text-primary">
                {magnet.kind === "werkzeug"
                  ? "Kostenlos anmelden – Ihr Zugang zur Plattform"
                  : "Jetzt anfordern"}
              </h2>
                <div className="mt-6">
                  <LeadMagnetForm magnet={magnet} />
                </div>
              </div>

              {/* Ticket W11: Personenkarten. Ohne Zuordnung erscheint hier nichts. */}
              <OfferPeople people={magnet.people} heading="Dahinter stehen" />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
